import JSZip from 'jszip';
import { Editor } from '../core/Editor';
import { AudioHelper, CuratedTrack, TrendingTrack, TrendingCategory } from '../audio/AudioHelper';
import {
  BorderGenerator,
  BorderPromptConfig,
  TransparencyWindowConfig
} from '../borders/BorderGenerator';

export class OverlayStudioModal {
  private editor: Editor;
  private container: HTMLDivElement | null = null;

  // Active step / tab: 'portrait' -> 'landscape' -> 'audio' -> 'export'
  private activeTab: 'portrait' | 'landscape' | 'audio' | 'export' = 'portrait';
  private orientation: 'portrait' | 'landscape' = 'portrait';
  private rawBorderImg: HTMLImageElement | null = null;
  private portraitCanvas: HTMLCanvasElement | null = null;
  private landscapeCanvas: HTMLCanvasElement | null = null;

  // Event Logo / Overlay Graphic State
  private logoImg: HTMLImageElement | null = null;
  private logoName: string = '🎃 Trunk or Treat 2026';
  // Portrait logo position (centered horizontally near bottom or top)
  private portraitLogoX: number = 540;
  private portraitLogoY: number = 1720;
  private portraitLogoScale: number = 1.0;
  // Landscape logo position (user can copy & move around freely)
  private landscapeLogoX: number = 960;
  private landscapeLogoY: number = 120;
  private landscapeLogoScale: number = 0.85;

  // Interactive logo drag state
  private isDraggingLogo: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private logoStartPosX: number = 0;
  private logoStartPosY: number = 0;

  // Window settings for portrait
  private windowConfig: TransparencyWindowConfig = {
    mode: 'inset',
    topInset: 240,
    bottomInset: 280,
    leftInset: 90,
    rightInset: 90,
    cornerRadius: 32,
    feather: 2,
    keyThreshold: 24,
    innerStroke: true,
    innerStrokeColor: '#ffd700',
    innerStrokeWidth: 2
  };

  // Window settings for landscape
  private landscapeWindowConfig: TransparencyWindowConfig = {
    mode: 'inset',
    topInset: 140,
    bottomInset: 140,
    leftInset: 240,
    rightInset: 240,
    cornerRadius: 28,
    feather: 2,
    keyThreshold: 24,
    innerStroke: true,
    innerStrokeColor: '#ffd700',
    innerStrokeWidth: 2
  };

  // Audio settings
  private activeAudioBuffer: AudioBuffer | null = null;
  private slicedAudioBuffer: AudioBuffer | null = null;
  private audioStartTime: number = 0;
  private readonly clipDuration: number = 7.0; // 7 seconds
  private isAudioPlaying: boolean = false;
  private audioSourceNode: AudioBufferSourceNode | null = null;
  private selectedTrackName: string = 'Trunk & Treat Spooky Trap';
  private selectedTrackId: string = 'spooky_bounce';
  private selectedTrackArtwork: string = '';

  // Trending Instagram / Reels Audio Explorer
  private trendingTracks: TrendingTrack[] = [];
  private isSearchingAudio: boolean = false;
  private activeAudioCategory: string = 'viral';
  private previewAudioEl: HTMLAudioElement | null = null;
  private previewPlayingTrackId: string | null = null;

  // Sample guest photo for live preview
  private previewWithSampleGuest: boolean = true;
  private sampleGuestImg: HTMLImageElement | null = null;

  constructor(editor: Editor) {
    this.editor = editor;
    this.loadSampleGuestImage();
    this.createDefaultLogo('trunk_treat');
  }

  private loadSampleGuestImage() {
    this.sampleGuestImg = new Image();
    this.sampleGuestImg.src = '/sample-portrait.jpg';
    this.sampleGuestImg.onload = () => {
      this.updatePreviewCanvas();
    };
  }

  private createDefaultLogo(type: 'trunk_treat' | 'spider_vip' | 'disco_360') {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 180;
    const ctx = canvas.getContext('2d')!;

    if (type === 'trunk_treat') {
      this.logoName = '🎃 Trunk or Treat 2026';
      ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
      ctx.beginPath();
      ctx.roundRect(8, 8, 584, 164, 20);
      ctx.fill();
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 4;
      ctx.stroke();

      ctx.font = 'bold 44px Outfit, sans-serif';
      ctx.fillStyle = '#f8fafc';
      ctx.textAlign = 'center';
      ctx.fillText('🎃 TRUNK OR TREAT', 300, 75);

      ctx.font = 'bold 26px Inter, sans-serif';
      ctx.fillStyle = '#22c55e';
      ctx.fillText('ANNUAL FESTIVAL 2026', 300, 130);
    } else if (type === 'spider_vip') {
      this.logoName = '🕷️ UV Cyber Spider VIP';
      ctx.fillStyle = 'rgba(10, 15, 29, 0.94)';
      ctx.beginPath();
      ctx.roundRect(8, 8, 584, 164, 20);
      ctx.fill();
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 4;
      ctx.stroke();

      ctx.font = 'bold 42px Outfit, sans-serif';
      ctx.fillStyle = '#22c55e';
      ctx.textAlign = 'center';
      ctx.fillText('🕷️ UV CYBER SPIDER', 300, 75);

      ctx.font = '600 24px Inter, sans-serif';
      ctx.fillStyle = '#cbd5e1';
      ctx.fillText('VIP ALL-ACCESS PASS', 300, 130);
    } else {
      this.logoName = '🪩 360 Video Booth';
      ctx.fillStyle = 'rgba(30, 27, 75, 0.94)';
      ctx.beginPath();
      ctx.roundRect(8, 8, 584, 164, 20);
      ctx.fill();
      ctx.strokeStyle = '#a855f7';
      ctx.lineWidth = 4;
      ctx.stroke();

      ctx.font = 'bold 42px Outfit, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText('🪩 360 VIDEO BOOTH', 300, 75);

      ctx.font = 'bold 26px Inter, sans-serif';
      ctx.fillStyle = '#e879f9';
      ctx.fillText('CELEBRATION REEL', 300, 130);
    }

    const img = new Image();
    img.onload = () => {
      this.logoImg = img;
      this.updateProcessedCanvases();
    };
    img.src = canvas.toDataURL();
  }

  public open(initialTab: 'portrait' | 'landscape' | 'audio' | 'export' = 'portrait') {
    this.close();
    this.activeTab = initialTab;
    this.orientation = initialTab === 'landscape' ? 'landscape' : 'portrait';

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop overlay-studio-backdrop';
    overlay.id = 'overlay-studio-modal';

    overlay.innerHTML = `
      <div class="modal-card overlay-studio-card">
        <!-- Modal Top Bar -->
        <div class="modal-header studio-modal-header">
          <div class="modal-title-group">
            <div class="modal-icon studio-icon">🎞️</div>
            <div>
              <h3>Photo Frame & 7s Reel Studio</h3>
              <p class="modal-subtitle">Make Portrait (1080×1920) &rarr; Landscape (1920×1080) with Moveable Logo &rarr; 7s Audio Reel &rarr; ZIP Package</p>
            </div>
          </div>
          <button class="modal-close-btn" id="close-overlay-studio">&times;</button>
        </div>

        <!-- Studio 4-Step Navigation Tabs Bar -->
        <div class="studio-tabs-bar">
          <button type="button" class="studio-tab-btn ${this.activeTab === 'portrait' ? 'active' : ''}" data-tab="portrait" id="tab-btn-portrait">
            <span>📱 1. Portrait Frame (1080×1920)</span>
          </button>
          <button type="button" class="studio-tab-btn ${this.activeTab === 'landscape' ? 'active' : ''}" data-tab="landscape" id="tab-btn-landscape">
            <span>💻 2. Landscape Frame (1920×1080)</span>
          </button>
          <button type="button" class="studio-tab-btn ${this.activeTab === 'audio' ? 'active' : ''}" data-tab="audio" id="tab-btn-audio">
            <span>🎵 3. 7s Audio Reel</span>
          </button>
          <button type="button" class="studio-tab-btn ${this.activeTab === 'export' ? 'active' : ''}" data-tab="export" id="tab-btn-export">
            <span>📦 4. Package & Download (.ZIP)</span>
          </button>
        </div>

        <!-- Studio Main Body (Two Columns: Controls on left, Live Canvas Preview on right) -->
        <div class="studio-content-layout">
          <!-- Left Column: Tab Controls -->
          <div class="studio-controls-pane">

            <!-- STEP 1: PORTRAIT FRAME (1080 × 1920) -->
            <div class="tab-page ${this.activeTab === 'portrait' ? 'active' : ''}" id="page-portrait">
              <!-- Step Description Header -->
              <div class="step-guide-card">
                <span class="step-num-pill">STEP 1 OF 4</span>
                <span class="step-headline">Create Portrait Frame (1080 × 1920 px)</span>
                <p class="step-sub">
                  Design the vertical 9:16 frame for Instagram Reels, TikTok, and 360 Video Booths. Customize the top/sides/bottom decor, window cutout, and event logo.
                </p>
              </div>

              <!-- Grok AI Generator Form -->
              <div class="form-group-card">
                <div class="group-title-row">
                  <span class="group-title">Grok AI Border Generator</span>
                  <span class="group-badge xai-badge">xAI Powered</span>
                </div>

                <!-- API Key Row -->
                <div class="field-item">
                  <div class="field-header">
                    <label>xAI (Grok) API Key</label>
                    <span class="field-hint">Pre-configured with your key</span>
                  </div>
                  <div class="api-key-input-box">
                    <input type="password" id="grok-api-key" class="styled-input" value="${BorderGenerator.getStoredApiKey()}" />
                    <button type="button" class="toggle-pwd-btn" id="btn-toggle-key-vis" title="Toggle visibility">👁️</button>
                  </div>
                </div>

                <!-- Quick Presets -->
                <div class="field-item">
                  <label>Popular Event Presets</label>
                  <div class="border-preset-chips">
                    <button type="button" class="chip-item active" data-preset="neon_spider">🕷️ Neon Cyber Spider</button>
                    <button type="button" class="chip-item" data-preset="trunk_treat">🎃 Trunk or Treat 2026</button>
                    <button type="button" class="chip-item" data-preset="gold_luxury">💍 Gold Luxury Wedding</button>
                    <button type="button" class="chip-item" data-preset="vip_360">🪩 VIP 360 Booth Glow</button>
                    <button type="button" class="chip-item" data-preset="gothic_vamp">🦇 Gothic Cobwebs</button>
                  </div>
                </div>

                <!-- Theme Prompt -->
                <div class="field-item">
                  <label>Event Theme / Style</label>
                  <input type="text" id="border-theme" class="styled-input" value="Neon Cyber Spider UV Glow Trunk or Treat" placeholder="e.g. Neon Cyber Spider UV Glow" />
                </div>

                <!-- Section Details -->
                <div class="border-sections-grid">
                  <div class="field-item">
                    <label>Top Border Elements</label>
                    <input type="text" id="border-top" class="styled-input" value="Neon spiderwebs, glowing spiders, and TRUNK OR TREAT text" />
                  </div>
                  <div class="field-item">
                    <label>Sides Border Elements</label>
                    <input type="text" id="border-sides" class="styled-input" value="Neon green dripping slime and cobweb strands" />
                  </div>
                  <div class="field-item full-width">
                    <label>Bottom Border Elements</label>
                    <input type="text" id="border-bottom" class="styled-input" value="Jack-o-lanterns, glowing neon 2026, and candy corn accents" />
                  </div>
                </div>

                <!-- Action Button -->
                <div class="generate-btn-row">
                  <button type="button" class="btn btn-primary generate-border-btn" id="btn-generate-border">
                    <span id="gen-icon">⚡</span>
                    <span id="gen-text">Generate Portrait Border with Grok</span>
                  </button>
                  <button type="button" class="btn btn-secondary upload-border-btn" id="btn-upload-border" title="Upload an existing border image">
                    📁 Upload Border
                  </button>
                  <input type="file" id="border-file-input" accept="image/*" style="display: none;" />
                </div>
              </div>

              <!-- Transparency Window Settings (Portrait) -->
              <div class="form-group-card">
                <div class="group-title-row">
                  <span class="group-title">Portrait Window Cutout</span>
                  <span class="group-badge">Transparent Guest Area</span>
                </div>

                <div class="window-sliders-box">
                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Top Margin (px):</span>
                      <span class="slider-val" id="val-top-inset">240px</span>
                    </div>
                    <input type="range" id="slider-top-inset" min="50" max="600" value="240" class="styled-range" />
                  </div>

                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Bottom Margin (px):</span>
                      <span class="slider-val" id="val-bottom-inset">280px</span>
                    </div>
                    <input type="range" id="slider-bottom-inset" min="50" max="600" value="280" class="styled-range" />
                  </div>

                  <div class="slider-dual-row">
                    <div class="slider-row">
                      <div class="slider-header">
                        <span>Side Margins:</span>
                        <span class="slider-val" id="val-sides-inset">90px</span>
                      </div>
                      <input type="range" id="slider-sides-inset" min="20" max="350" value="90" class="styled-range" />
                    </div>
                    <div class="slider-row">
                      <div class="slider-header">
                        <span>Corner Radius:</span>
                        <span class="slider-val" id="val-corner-radius">32px</span>
                      </div>
                      <input type="range" id="slider-corner-radius" min="0" max="150" value="32" class="styled-range" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Logo & Badge Customization (Portrait) -->
              <div class="form-group-card">
                <div class="group-title-row">
                  <span class="group-title">Event Logo & Badge</span>
                  <span class="group-badge">Moveable Overlay</span>
                </div>
                <p class="field-hint">
                  Place an event logo or title badge on your portrait frame. In Step 2, you can copy this logo to landscape and move it around!
                </p>

                <!-- Sample Logo Presets -->
                <div class="field-item">
                  <label>Sample Event Badges</label>
                  <div class="border-preset-chips">
                    <button type="button" class="chip-item active" id="btn-logo-preset-trunk">🎃 Trunk or Treat 2026</button>
                    <button type="button" class="chip-item" id="btn-logo-preset-spider">🕷️ Cyber Spider VIP</button>
                    <button type="button" class="chip-item" id="btn-logo-preset-disco">🪩 360 Video Booth</button>
                  </div>
                </div>

                <!-- Custom Logo Upload -->
                <div class="field-item">
                  <label>Or Upload Custom Logo Image (PNG / Transparent)</label>
                  <div class="logo-upload-row">
                    <button type="button" class="btn btn-secondary" id="btn-upload-logo">📁 Upload Custom Logo...</button>
                    <span class="logo-name-display" id="label-logo-name">${this.logoName}</span>
                    <input type="file" id="logo-file-input" accept="image/*" style="display: none;" />
                  </div>
                </div>

                <!-- Position Presets (Portrait) -->
                <div class="field-item">
                  <label>Quick Position on Portrait Frame:</label>
                  <div class="logo-pos-chips">
                    <button type="button" class="chip-item active" id="pos-port-bottom">⬇️ Bottom Center</button>
                    <button type="button" class="chip-item" id="pos-port-top">⬆️ Top Center</button>
                    <button type="button" class="chip-item" id="pos-port-top-left">↖️ Top Left</button>
                    <button type="button" class="chip-item" id="pos-port-top-right">↗️ Top Right</button>
                  </div>
                </div>

                <!-- Sliders for fine-tuning -->
                <div class="slider-dual-row">
                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Vertical Y:</span>
                      <span class="slider-val" id="val-port-logo-y">1720px</span>
                    </div>
                    <input type="range" id="slider-port-logo-y" min="100" max="1850" value="1720" class="styled-range" />
                  </div>
                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Logo Scale:</span>
                      <span class="slider-val" id="val-port-logo-scale">100%</span>
                    </div>
                    <input type="range" id="slider-port-logo-scale" min="30" max="200" value="100" class="styled-range" />
                  </div>
                </div>
              </div>

              <!-- Next Step Button -->
              <div class="step-next-row">
                <button type="button" class="btn btn-primary step-next-btn" id="btn-goto-step2">
                  <span>Proceed to 2. Landscape Frame &rarr;</span>
                </button>
              </div>
            </div>

            <!-- STEP 2: LANDSCAPE FRAME (1920 × 1080) & MOVEABLE LOGO -->
            <div class="tab-page ${this.activeTab === 'landscape' ? 'active' : ''}" id="page-landscape" style="display: none;">
              <!-- Step Description Header -->
              <div class="step-guide-card">
                <span class="step-num-pill">STEP 2 OF 4</span>
                <span class="step-headline">Create Landscape Frame (1920 × 1080 px)</span>
                <p class="step-sub">
                  Copy elements from your Portrait frame, then move the logo and adjust the frame for horizontal widescreen format!
                </p>
              </div>

              <!-- Copy / Transfer from Portrait Card -->
              <div class="form-group-card highlight-transfer-card">
                <div class="group-title-row">
                  <span class="group-title">📋 Adapt & Copy from Portrait</span>
                  <span class="group-badge">1-Click Sync</span>
                </div>
                <p class="field-hint">
                  Transfer the generated border style and your logo from Portrait directly into this Landscape frame:
                </p>
                <div class="transfer-btn-row">
                  <button type="button" class="btn btn-primary copy-portrait-btn" id="btn-copy-from-portrait">
                    <span>⚡ Copy Border & Logo from Portrait</span>
                  </button>
                </div>
              </div>

              <!-- Moveable Logo on Landscape Frame Card -->
              <div class="form-group-card logo-landscape-card">
                <div class="group-title-row">
                  <span class="group-title">🎯 Move & Position Logo on Landscape</span>
                  <span class="group-badge interactive-badge">Drag & Drop on Canvas</span>
                </div>
                <p class="field-hint">
                  💡 <strong>Drag the logo directly on the live preview canvas</strong> to place it anywhere, or use the quick buttons below:
                </p>

                <!-- Quick Position Presets for Landscape -->
                <div class="field-item">
                  <label>Quick Position Presets:</label>
                  <div class="logo-pos-chips">
                    <button type="button" class="chip-item active" id="pos-land-top-center">⬆️ Top Center</button>
                    <button type="button" class="chip-item" id="pos-land-top-left">↖️ Top Left</button>
                    <button type="button" class="chip-item" id="pos-land-top-right">↗️ Top Right</button>
                    <button type="button" class="chip-item" id="pos-land-bottom-center">⬇️ Bottom Center</button>
                    <button type="button" class="chip-item" id="pos-land-bottom-left">↙️ Bottom Left</button>
                    <button type="button" class="chip-item" id="pos-land-bottom-right">↘️ Bottom Right</button>
                  </div>
                </div>

                <!-- Fine-tuning Position & Scale Sliders -->
                <div class="slider-row">
                  <div class="slider-header">
                    <span>Horizontal Position (X):</span>
                    <span class="slider-val" id="val-land-logo-x">960px (Center)</span>
                  </div>
                  <input type="range" id="slider-land-logo-x" min="100" max="1820" value="960" class="styled-range" />
                </div>

                <div class="slider-dual-row">
                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Vertical Position (Y):</span>
                      <span class="slider-val" id="val-land-logo-y">120px</span>
                    </div>
                    <input type="range" id="slider-land-logo-y" min="60" max="1020" value="120" class="styled-range" />
                  </div>
                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Logo Scale:</span>
                      <span class="slider-val" id="val-land-logo-scale">85%</span>
                    </div>
                    <input type="range" id="slider-land-logo-scale" min="30" max="200" value="85" class="styled-range" />
                  </div>
                </div>
              </div>

              <!-- Landscape Window Cutout Settings -->
              <div class="form-group-card">
                <div class="group-title-row">
                  <span class="group-title">Landscape Window Cutout</span>
                  <span class="group-badge">16:9 Transparent Cutout</span>
                </div>

                <div class="window-sliders-box">
                  <div class="slider-dual-row">
                    <div class="slider-row">
                      <div class="slider-header">
                        <span>Top/Bottom Margins:</span>
                        <span class="slider-val" id="val-land-tb-inset">140px</span>
                      </div>
                      <input type="range" id="slider-land-tb-inset" min="40" max="350" value="140" class="styled-range" />
                    </div>
                    <div class="slider-row">
                      <div class="slider-header">
                        <span>Side Margins:</span>
                        <span class="slider-val" id="val-land-lr-inset">240px</span>
                      </div>
                      <input type="range" id="slider-land-lr-inset" min="40" max="500" value="240" class="styled-range" />
                    </div>
                  </div>
                  <div class="slider-row">
                    <div class="slider-header">
                      <span>Corner Radius:</span>
                      <span class="slider-val" id="val-land-corner">28px</span>
                    </div>
                    <input type="range" id="slider-land-corner" min="0" max="100" value="28" class="styled-range" />
                  </div>
                </div>
              </div>

              <!-- Navigation Row -->
              <div class="step-nav-row">
                <button type="button" class="btn btn-secondary" id="btn-backto-step1">&larr; Back to Portrait</button>
                <button type="button" class="btn btn-primary step-next-btn" id="btn-goto-step3">
                  <span>Proceed to 3. 7s Audio Reel &rarr;</span>
                </button>
              </div>
            </div>

            <!-- STEP 3: 7-SECOND AUDIO REEL EDITOR -->
            <div class="tab-page ${this.activeTab === 'audio' ? 'active' : ''}" id="page-audio" style="display: none;">
              <!-- Step Description Header -->
              <div class="step-guide-card">
                <span class="step-num-pill">STEP 3 OF 4</span>
                <span class="step-headline">Trending Audio & 7s Reel Hook</span>
                <p class="step-sub">
                  Discover trending sounds from Instagram Reels & TikTok, search any artist or song, or choose royalty-free procedural party beats.
                </p>
              </div>

              <!-- Instagram / Reels Trending Audio Explorer -->
              <div class="form-group-card trending-audio-card">
                <div class="group-title-row">
                  <div class="trending-title-wrap">
                    <span class="trending-badge-icon">🔥</span>
                    <span class="group-title">Trending Reels Audio</span>
                  </div>
                  <span class="group-badge">Live Music Explorer</span>
                </div>

                <!-- Search Input Bar -->
                <div class="audio-search-bar">
                  <div class="search-input-wrapper">
                    <span class="search-icon-symbol">🔍</span>
                    <input type="text" id="input-audio-search" class="styled-input audio-search-input" placeholder="Search songs, artists, or Reels trends (e.g. Espresso, Thriller, Drake, Phonk)..." />
                    <button type="button" class="btn-clear-search" id="btn-clear-search" title="Clear search">&times;</button>
                  </div>
                  <button type="button" class="btn btn-primary btn-search-audio" id="btn-search-audio">
                    <span>Search</span>
                  </button>
                </div>

                <!-- Category Chips Carousel -->
                <div class="audio-category-chips" id="audio-category-chips">
                  <!-- Populated dynamically: Viral, Halloween, 360 Party, Wedding, Phonk, Latin, Lo-Fi, Royalty-Free Beats -->
                </div>

                <!-- Search Status & Results -->
                <div class="trending-results-container" id="trending-results-container">
                  <div class="audio-loading-state" id="audio-loading-state" style="display: none;">
                    <div class="audio-spinner"></div>
                    <span>Finding trending audio clips...</span>
                  </div>
                  <div class="trending-tracks-grid" id="trending-tracks-grid">
                    <!-- Populated dynamically with track cards -->
                  </div>
                </div>
              </div>

              <!-- Curated Royalty-Free Synthesizer (Collapsible / Toggle) -->
              <div class="form-group-card curated-synth-card" id="curated-synth-card" style="display: none;">
                <div class="group-title-row">
                  <span class="group-title">🛡️ Royalty-Free Procedural Synth Library</span>
                  <span class="group-badge">100% Commercial Safe</span>
                </div>
                <p class="field-hint">
                  Procedurally synthesized directly in the browser via Web Audio API. 100% royalty-free for commercial client work:
                </p>
                <div class="curated-tracks-list" id="curated-tracks-list">
                  <!-- Populated dynamically -->
                </div>
              </div>

              <!-- Upload Custom Song -->
              <div class="form-group-card">
                <div class="group-title-row">
                  <span class="group-title">Or Upload Your Own Audio</span>
                  <span class="group-badge">MP3 / WAV / M4A</span>
                </div>
                <div class="upload-audio-zone" id="upload-audio-zone">
                  <span class="upload-audio-icon">🎧</span>
                  <div class="upload-audio-text">
                    <span class="audio-drag-title">Drop your song file here</span>
                    <span class="audio-drag-sub">or click to browse from your device</span>
                  </div>
                  <input type="file" id="audio-file-input" accept="audio/*" style="display: none;" />
                </div>
              </div>

              <!-- Visual Waveform & 7-Second Selector -->
              <div class="form-group-card waveform-card">
                <div class="group-title-row">
                  <div class="waveform-header-meta">
                    <img id="active-track-art" class="active-track-art" src="" alt="" style="display: none;" />
                    <div class="waveform-title-text">
                      <span class="group-title">7-Second Slice Trimmer</span>
                      <span class="active-track-name" id="label-active-track">Trunk & Treat Spooky Trap</span>
                    </div>
                  </div>
                  <span class="slice-len-badge" id="badge-clip-len">7.0s Clip</span>
                </div>

                <div class="waveform-container" id="waveform-interactive-container" title="Click anywhere on the waveform or drag the slider below to position your 7-second slice">
                  <canvas id="waveform-canvas" class="waveform-canvas" width="600" height="96"></canvas>
                  <div class="selection-overlay-7s" id="selection-overlay-7s">
                    <span class="slice-badge">7.0s Clip</span>
                  </div>
                </div>

                <!-- Trim Start Position Slider -->
                <div class="trim-slider-wrapper">
                  <div class="slider-header">
                    <span>Trim Start Position (Scrub through track):</span>
                    <span class="slider-val" id="val-trim-start">00:00.00</span>
                  </div>
                  <input type="range" id="slider-trim-start" min="0" max="23" step="0.1" value="0" class="styled-range" />
                </div>

                <!-- Timeline controls & Playback -->
                <div class="audio-controls-bar">
                  <button type="button" class="btn btn-primary audio-play-btn" id="btn-play-audio">
                    <span id="play-icon">▶</span>
                    <span id="play-text">Play 7s Loop</span>
                  </button>

                  <div class="time-readout-group">
                    <div class="time-item">
                      <span class="t-lbl">Start:</span>
                      <span class="t-val" id="time-start">00:00.00</span>
                    </div>
                    <div class="time-item">
                      <span class="t-lbl">End:</span>
                      <span class="t-val" id="time-end">00:07.00</span>
                    </div>
                    <div class="time-item highlight">
                      <span class="t-lbl">Duration:</span>
                      <span class="t-val">7.00s</span>
                    </div>
                  </div>

                  <button type="button" class="btn btn-secondary download-audio-direct" id="btn-export-audio-only" title="Download the 7-second audio clip as WAV">
                    ⬇️ Download 7s WAV
                  </button>
                </div>
              </div>

              <!-- Navigation Row -->
              <div class="step-nav-row">
                <button type="button" class="btn btn-secondary" id="btn-backto-step2">&larr; Back to Landscape</button>
                <button type="button" class="btn btn-primary step-next-btn" id="btn-goto-step4">
                  <span>Proceed to 4. Package & Export &rarr;</span>
                </button>
              </div>
            </div>

            <!-- STEP 4: PACKAGE & EXPORT -->
            <div class="tab-page ${this.activeTab === 'export' ? 'active' : ''}" id="page-export" style="display: none;">
              <!-- Step Description Header -->
              <div class="step-guide-card">
                <span class="step-num-pill">STEP 4 OF 4</span>
                <span class="step-headline">Complete Package & Delivery</span>
                <p class="step-sub">
                  Your portrait frame, landscape frame, and 7-second audio clip are packaged and ready to export as a single bundled .ZIP file!
                </p>
              </div>

              <div class="form-group-card export-package-card">
                <div class="group-title-row">
                  <span class="group-title">Package Summary</span>
                  <span class="group-badge package-badge">Ready for Delivery</span>
                </div>

                <div class="package-contents-list">
                  <div class="pkg-item">
                    <span class="pkg-icon">📱</span>
                    <div class="pkg-details">
                      <span class="pkg-name">overlay_portrait_1080x1920.png</span>
                      <span class="pkg-sub">Vertical transparent PNG frame with positioned logo</span>
                    </div>
                    <span class="pkg-status ready">Ready</span>
                  </div>

                  <div class="pkg-item">
                    <span class="pkg-icon">💻</span>
                    <div class="pkg-details">
                      <span class="pkg-name">overlay_landscape_1920x1080.png</span>
                      <span class="pkg-sub">Widescreen transparent PNG frame with repositioned logo</span>
                    </div>
                    <span class="pkg-status ready">Ready</span>
                  </div>

                  <div class="pkg-item">
                    <span class="pkg-icon">🎵</span>
                    <div class="pkg-details">
                      <span class="pkg-name">audio_clip_7s.wav</span>
                      <span class="pkg-sub">Lossless 16-bit 44.1kHz 7-second audio loop</span>
                    </div>
                    <span class="pkg-status ready">Ready</span>
                  </div>

                  <div class="pkg-item">
                    <span class="pkg-icon">📋</span>
                    <div class="pkg-details">
                      <span class="pkg-name">package_info.json</span>
                      <span class="pkg-sub">Theme metadata, timestamps, and frame dimensions</span>
                    </div>
                    <span class="pkg-status ready">Ready</span>
                  </div>
                </div>

                <!-- Big ZIP Action Button -->
                <button type="button" class="btn btn-primary big-zip-btn" id="btn-download-zip">
                  <span class="zip-icon">📦</span>
                  <div class="zip-text-group">
                    <span class="zip-title">Download Complete Package (.ZIP)</span>
                    <span class="zip-sub">Bundles Portrait PNG + Landscape PNG + 7s Audio WAV</span>
                  </div>
                </button>
              </div>

              <!-- Individual Export Actions -->
              <div class="form-group-card">
                <span class="group-title">Individual Downloads & Canvas Import</span>
                <div class="individual-actions-grid">
                  <button type="button" class="btn btn-secondary" id="btn-download-portrait">
                    📱 Download Portrait (1080×1920)
                  </button>
                  <button type="button" class="btn btn-secondary" id="btn-download-landscape">
                    💻 Download Landscape (1920×1080)
                  </button>
                  <button type="button" class="btn btn-secondary" id="btn-download-audio-tab4">
                    🎵 Download 7s Audio (.WAV)
                  </button>
                  <button type="button" class="btn btn-secondary highlight-btn" id="btn-open-in-ironcrop">
                    🎨 Open in Iron Crop Canvas
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: Live Interactive Preview -->
          <div class="studio-preview-pane">
            <div class="preview-header-bar">
              <span class="preview-title" id="preview-mode-title">Live Preview (Portrait 1080 × 1920)</span>
              <div class="preview-toggles">
                <label class="toggle-guest-label" title="Toggle sample guest photo behind transparent window">
                  <input type="checkbox" id="chk-preview-guest" checked />
                  <span>Guest Photo</span>
                </label>
              </div>
            </div>

            <div class="preview-canvas-viewport">
              <canvas id="studio-preview-canvas" width="1080" height="1920" class="studio-preview-canvas" title="Click and drag logo to reposition it!"></canvas>
            </div>

            <div class="preview-footer-info">
              <span id="preview-res-indicator">1080 × 1920 px • Transparent Window • Moveable Logo Active</span>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.container = overlay;

    this.attachEvents(overlay);
    this.initDefaultContent();
  }

  private initDefaultContent() {
    this.selectCuratedTrack('spooky_bounce');
    this.renderCuratedTracksList();
    this.initTrendingAudioExplorer();
    this.createStarterBorder();
  }

  private createStarterBorder() {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext('2d')!;

    const grad = ctx.createLinearGradient(0, 0, 1080, 1920);
    grad.addColorStop(0, '#0f172a');
    grad.addColorStop(0.5, '#1e1b4b');
    grad.addColorStop(1, '#090d16');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1080, 1920);

    // Decorative cobwebs & accents
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 3;
    ctx.shadowColor = '#22c55e';
    ctx.shadowBlur = 15;

    ctx.font = 'bold 54px Outfit, sans-serif';
    ctx.fillStyle = '#f8fafc';
    ctx.textAlign = 'center';
    ctx.fillText('TRUNK OR TREAT', 540, 130);

    ctx.font = '600 28px Inter, sans-serif';
    ctx.fillStyle = '#22c55e';
    ctx.fillText('🕸️ 360 PHOTOBOOTH REEL 🕸️', 540, 185);

    ctx.font = 'bold 44px Outfit, sans-serif';
    ctx.fillStyle = '#f59e0b';
    ctx.fillText('🎃 HALLOWEEN 2026 🎃', 540, 1780);

    ctx.font = '500 24px Inter, sans-serif';
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('TAG US @PHOTOBOOTH • PHOTO.DRB.WANG', 540, 1835);

    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 4;
    ctx.strokeRect(40, 40, 1000, 1840);

    const img = new Image();
    img.onload = () => {
      this.rawBorderImg = img;
      this.updateProcessedCanvases();
    };
    img.src = canvas.toDataURL();
  }

  private updateProcessedCanvases() {
    if (!this.rawBorderImg) return;

    // 1. Process portrait version (1080x1920)
    this.portraitCanvas = BorderGenerator.processTransparencyWindow(
      this.rawBorderImg,
      1080,
      1920,
      this.windowConfig
    );

    // 2. Process landscape version (1920x1080)
    this.landscapeCanvas = BorderGenerator.adaptToLandscape(
      this.portraitCanvas,
      this.landscapeWindowConfig
    );

    this.updatePreviewCanvas();
  }

  private updatePreviewCanvas() {
    const previewCanvas = this.container?.querySelector('#studio-preview-canvas') as HTMLCanvasElement;
    if (!previewCanvas) return;

    const ctx = previewCanvas.getContext('2d')!;
    const isPortrait = this.orientation === 'portrait';
    const sourceCanvas = isPortrait ? this.portraitCanvas : this.landscapeCanvas;

    const targetW = isPortrait ? 1080 : 1920;
    const targetH = isPortrait ? 1920 : 1080;

    if (previewCanvas.width !== targetW || previewCanvas.height !== targetH) {
      previewCanvas.width = targetW;
      previewCanvas.height = targetH;
    }

    ctx.clearRect(0, 0, targetW, targetH);

    // 1. Checkerboard backdrop
    const patternSize = 24;
    for (let y = 0; y < targetH; y += patternSize) {
      for (let x = 0; x < targetW; x += patternSize) {
        const isEven = (Math.floor(x / patternSize) + Math.floor(y / patternSize)) % 2 === 0;
        ctx.fillStyle = isEven ? '#f1f5f9' : '#cbd5e1';
        ctx.fillRect(x, y, patternSize, patternSize);
      }
    }

    // 2. Sample guest photo behind transparent cutout
    if (this.previewWithSampleGuest && this.sampleGuestImg && this.sampleGuestImg.complete) {
      ctx.save();
      const guestRatio = this.sampleGuestImg.naturalWidth / this.sampleGuestImg.naturalHeight;
      const canvasRatio = targetW / targetH;
      let drawW = targetW;
      let drawH = targetH;
      let drawX = 0;
      let drawY = 0;

      if (guestRatio > canvasRatio) {
        drawH = targetH;
        drawW = targetH * guestRatio;
        drawX = (targetW - drawW) / 2;
      } else {
        drawW = targetW;
        drawH = targetW / guestRatio;
        drawY = (targetH - drawH) / 2;
      }

      ctx.drawImage(this.sampleGuestImg, drawX, drawY, drawW, drawH);
      ctx.restore();
    }

    // 3. Draw overlay border with transparency window
    if (sourceCanvas) {
      ctx.drawImage(sourceCanvas, 0, 0, targetW, targetH);
    }

    // 4. Draw Logo / Event Overlay Graphic
    if (this.logoImg && this.logoImg.complete) {
      const curX = isPortrait ? this.portraitLogoX : this.landscapeLogoX;
      const curY = isPortrait ? this.portraitLogoY : this.landscapeLogoY;
      const curScale = isPortrait ? this.portraitLogoScale : this.landscapeLogoScale;

      const baseW = (isPortrait ? 400 : 380) * curScale;
      const ratio = this.logoImg.naturalHeight / this.logoImg.naturalWidth;
      const baseH = baseW * ratio;
      const x = curX - baseW / 2;
      const y = curY - baseH / 2;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 12;
      ctx.drawImage(this.logoImg, x, y, baseW, baseH);
      ctx.restore();

      // Draw dashed selection rectangle & corner handles to indicate moveability
      if (this.activeTab === 'portrait' || this.activeTab === 'landscape') {
        ctx.save();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2.5;
        ctx.setLineDash([8, 6]);
        ctx.strokeRect(x - 6, y - 6, baseW + 12, baseH + 12);

        // Handles
        ctx.setLineDash([]);
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#1d4ed8';
        ctx.lineWidth = 2;
        const hSize = 8;
        const handlePts = [
          [x - 6, y - 6],
          [x + baseW + 6, y - 6],
          [x - 6, y + baseH + 6],
          [x + baseW + 6, y + baseH + 6],
          [curX, y - 6] // Top center
        ];
        handlePts.forEach(([hx, hy]) => {
          ctx.fillRect(hx - hSize / 2, hy - hSize / 2, hSize, hSize);
          ctx.strokeRect(hx - hSize / 2, hy - hSize / 2, hSize, hSize);
        });
        ctx.restore();
      }
    }
  }

  private initTrendingAudioExplorer() {
    this.renderAudioCategoryChips();
    this.setupAudioSearchEvents();
    this.setupWaveformScrubbing();
    // Default initial query with popular viral reels hits
    this.searchTrendingMusic('top hits viral reels');
  }

  private renderAudioCategoryChips() {
    const container = this.container?.querySelector('#audio-category-chips');
    if (!container) return;

    const categories = AudioHelper.getTrendingCategories();
    let html = categories
      .map(
        c => `
        <button type="button" class="category-chip-btn ${this.activeAudioCategory === c.id ? 'active' : ''}" data-id="${c.id}" data-query="${c.searchQuery}">
          <span>${c.icon}</span>
          <span>${c.label}</span>
        </button>
      `
      )
      .join('');

    html += `
      <button type="button" class="category-chip-btn synth-chip ${this.activeAudioCategory === 'synth' ? 'active' : ''}" data-id="synth">
        <span>🛡️</span>
        <span>Royalty-Free Beats</span>
      </button>
    `;

    container.innerHTML = html;

    container.querySelectorAll('.category-chip-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.dataset.id;
        const query = target.dataset.query;
        if (!id) return;

        this.activeAudioCategory = id;
        this.renderAudioCategoryChips();

        const synthCard = this.container?.querySelector('#curated-synth-card') as HTMLElement;

        if (id === 'synth') {
          if (synthCard) {
            synthCard.style.display = 'block';
            synthCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        } else {
          if (synthCard) synthCard.style.display = 'none';
          if (query) {
            const input = this.container?.querySelector('#input-audio-search') as HTMLInputElement;
            if (input) input.value = query;
            this.searchTrendingMusic(query);
          }
        }
      });
    });
  }

  private setupAudioSearchEvents() {
    const input = this.container?.querySelector('#input-audio-search') as HTMLInputElement;
    const btnSearch = this.container?.querySelector('#btn-search-audio');
    const btnClear = this.container?.querySelector('#btn-clear-search') as HTMLElement;

    const doSearch = () => {
      const q = input?.value.trim();
      if (q) {
        this.searchTrendingMusic(q);
      }
    };

    btnSearch?.addEventListener('click', doSearch);
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        doSearch();
      }
    });

    input?.addEventListener('input', () => {
      if (btnClear) {
        btnClear.style.display = input.value.length > 0 ? 'block' : 'none';
      }
    });

    btnClear?.addEventListener('click', () => {
      if (input) {
        input.value = '';
        input.focus();
        btnClear.style.display = 'none';
        this.searchTrendingMusic('top hits viral reels');
      }
    });
  }

  private async searchTrendingMusic(query: string) {
    if (this.isSearchingAudio) return;
    this.isSearchingAudio = true;

    const loadingState = this.container?.querySelector('#audio-loading-state') as HTMLElement;
    const grid = this.container?.querySelector('#trending-tracks-grid') as HTMLElement;

    if (loadingState) loadingState.style.display = 'flex';
    if (grid) grid.innerHTML = '';

    try {
      this.trendingTracks = await AudioHelper.searchTrendingAudio(query, 24);
      this.renderTrendingTracksList();
    } catch (err: any) {
      console.error(err);
      if (grid) {
        grid.innerHTML = `
          <div style="padding: 24px; text-align: center; color: var(--text-muted); font-size: 12px;">
            ⚠️ Could not load search results. Please check network connection or try another keyword.
          </div>
        `;
      }
    } finally {
      this.isSearchingAudio = false;
      if (loadingState) loadingState.style.display = 'none';
    }
  }

  private renderTrendingTracksList() {
    const grid = this.container?.querySelector('#trending-tracks-grid');
    if (!grid) return;

    if (this.trendingTracks.length === 0) {
      grid.innerHTML = `
        <div style="padding: 24px; text-align: center; color: var(--text-muted); font-size: 12px;">
          No trending tracks found for this search. Try searching an artist, song title, or genre!
        </div>
      `;
      return;
    }

    grid.innerHTML = this.trendingTracks
      .map(t => {
        const isSelected = t.id === this.selectedTrackId;
        const isPreviewing = this.previewPlayingTrackId === t.id;
        return `
          <div class="trending-track-card ${isSelected ? 'active-selected' : ''}" data-id="${t.id}">
            <img src="${t.artwork || '/favicon.ico'}" alt="${t.title}" class="track-art-img" onerror="this.style.opacity='0.2'" />
            <div class="track-info-col">
              <span class="track-song-title" title="${t.title}">${t.title}</span>
              <span class="track-artist-line" title="${t.artist}">${t.artist} • ${t.album || ''}</span>
              <span class="track-genre-tag">${t.genre}</span>
            </div>
            <div class="track-actions-col">
              <button type="button" class="btn-preview-audio ${isPreviewing ? 'playing' : ''}" data-id="${t.id}" title="${isPreviewing ? 'Pause 30s preview' : 'Play 30s preview'}">
                <span>${isPreviewing ? '⏸ Pause' : '▶ Preview'}</span>
              </button>
              <button type="button" class="btn-use-track" data-id="${t.id}" title="Slice this sound into 7s reel">
                <span>${isSelected ? '✓ In Trimmer' : '⚡ Use 7s'}</span>
              </button>
            </div>
          </div>
        `;
      })
      .join('');

    // Attach preview events
    grid.querySelectorAll('.btn-preview-audio').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        const track = this.trendingTracks.find(t => t.id === id);
        if (track) this.toggleTrackPreview(track);
      });
    });

    // Attach use track events
    grid.querySelectorAll('.btn-use-track, .trending-track-card').forEach(el => {
      el.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        const track = this.trendingTracks.find(t => t.id === id);
        if (track) this.selectTrendingTrack(track);
      });
    });
  }

  private toggleTrackPreview(track: TrendingTrack) {
    if (this.previewPlayingTrackId === track.id) {
      this.stopTrackPreview();
      return;
    }

    this.stopTrackPreview();
    this.stopAudio(); // Stop main loop if playing so audio doesn't clash

    this.previewAudioEl = new Audio(track.previewUrl);
    this.previewPlayingTrackId = track.id;

    this.previewAudioEl.addEventListener('ended', () => {
      this.stopTrackPreview();
    });

    this.previewAudioEl.addEventListener('error', () => {
      this.stopTrackPreview();
      this.editor.showToast('Preview stream unavailable for this track');
    });

    this.previewAudioEl.play().catch(err => {
      console.warn('Audio preview autoplay prevented:', err);
      this.stopTrackPreview();
    });

    this.renderTrendingTracksList();
  }

  private stopTrackPreview() {
    if (this.previewAudioEl) {
      try {
        this.previewAudioEl.pause();
        this.previewAudioEl.currentTime = 0;
      } catch (_) {}
      this.previewAudioEl = null;
    }
    this.previewPlayingTrackId = null;
    this.renderTrendingTracksList();
  }

  private async selectTrendingTrack(track: TrendingTrack) {
    this.stopTrackPreview();
    this.stopAudio();

    this.selectedTrackId = track.id;
    this.selectedTrackName = `${track.title} - ${track.artist}`;
    this.selectedTrackArtwork = track.artwork;
    this.editor.showToast(`Loading "${track.title}" into 7s trimmer...`);

    try {
      const buffer = await AudioHelper.fetchAndDecodeAudio(track.previewUrl);
      this.activeAudioBuffer = buffer;
      this.audioStartTime = 0;
      this.slicedAudioBuffer = AudioHelper.sliceAudio(buffer, 0, this.clipDuration);

      this.updateActiveTrackBanner();
      this.updateTrimSliderBounds();
      this.renderWaveform();
      this.renderTrendingTracksList();
      this.editor.showToast(`✨ Loaded "${track.title}"! Waveform ready for 7-second trimming.`);
    } catch (err: any) {
      console.error(err);
      this.editor.showToast(`Error decoding audio: ${err.message || 'Stream blocked'}`);
    }
  }

  private updateActiveTrackBanner() {
    const label = this.container?.querySelector('#label-active-track');
    const art = this.container?.querySelector('#active-track-art') as HTMLImageElement;

    if (label) {
      label.textContent = this.selectedTrackName;
    }

    if (art) {
      if (this.selectedTrackArtwork) {
        art.src = this.selectedTrackArtwork;
        art.style.display = 'block';
      } else {
        art.style.display = 'none';
      }
    }
  }

  private setupWaveformScrubbing() {
    const slider = this.container?.querySelector('#slider-trim-start') as HTMLInputElement;
    const canvas = this.container?.querySelector('#waveform-canvas') as HTMLCanvasElement;

    slider?.addEventListener('input', (e: any) => {
      if (!this.activeAudioBuffer) return;
      this.audioStartTime = parseFloat(e.target.value);
      this.onTrimPositionChanged();
    });

    if (canvas) {
      let isDragging = false;

      const handleScrub = (e: MouseEvent) => {
        if (!this.activeAudioBuffer) return;
        const rect = canvas.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const maxStart = Math.max(0, this.activeAudioBuffer.duration - this.clipDuration);
        this.audioStartTime = ratio * maxStart;

        if (slider) slider.value = this.audioStartTime.toString();
        this.onTrimPositionChanged();
      };

      canvas.addEventListener('mousedown', (e) => {
        isDragging = true;
        handleScrub(e);
      });

      window.addEventListener('mousemove', (e) => {
        if (isDragging) handleScrub(e);
      });

      window.addEventListener('mouseup', () => {
        if (isDragging) isDragging = false;
      });
    }
  }

  private onTrimPositionChanged() {
    if (!this.activeAudioBuffer) return;

    this.slicedAudioBuffer = AudioHelper.sliceAudio(
      this.activeAudioBuffer,
      this.audioStartTime,
      this.clipDuration
    );

    const val = this.container?.querySelector('#val-trim-start');
    if (val) val.textContent = this.formatSeconds(this.audioStartTime);

    this.updateWaveformSelectionBox();

    // If currently looping, restart seamlessly at the new trim position
    if (this.isAudioPlaying) {
      this.playAudioLoop();
    }
  }

  private updateTrimSliderBounds() {
    const slider = this.container?.querySelector('#slider-trim-start') as HTMLInputElement;
    const val = this.container?.querySelector('#val-trim-start');
    if (!slider || !this.activeAudioBuffer) return;

    const maxStart = Math.max(0, this.activeAudioBuffer.duration - this.clipDuration);
    slider.min = '0';
    slider.max = maxStart.toFixed(1);
    slider.value = this.audioStartTime.toString();

    if (val) val.textContent = this.formatSeconds(this.audioStartTime);
  }

  private renderCuratedTracksList() {
    const list = this.container?.querySelector('#curated-tracks-list');
    if (!list) return;

    const tracks = AudioHelper.getCuratedTracks();
    list.innerHTML = tracks
      .map(
        t => `
        <div class="curated-track-item ${t.id === this.selectedTrackId ? 'active' : ''}" data-id="${t.id}">
          <span class="track-icon">${t.icon}</span>
          <div class="track-meta">
            <span class="track-title">${t.name}</span>
            <span class="track-desc">${t.bpm} BPM • ${t.description}</span>
          </div>
          <button type="button" class="btn btn-secondary mini-select-btn" data-id="${t.id}">
            ${t.id === this.selectedTrackId ? 'Selected' : 'Use Hook'}
          </button>
        </div>
      `
      )
      .join('');

    list.querySelectorAll('.mini-select-btn, .curated-track-item').forEach(el => {
      el.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) this.selectCuratedTrack(id);
      });
    });
  }

  private selectCuratedTrack(trackId: string) {
    const track = AudioHelper.getCuratedTracks().find(t => t.id === trackId);
    if (!track) return;

    this.stopTrackPreview();
    this.stopAudio();

    this.selectedTrackId = track.id;
    this.selectedTrackName = `${track.icon} ${track.name}`;
    this.selectedTrackArtwork = '';

    const buffer = AudioHelper.synthesizeCuratedTrack(trackId);
    this.activeAudioBuffer = buffer;
    this.slicedAudioBuffer = AudioHelper.sliceAudio(buffer, 0, this.clipDuration);
    this.audioStartTime = 0;

    this.updateActiveTrackBanner();
    this.updateTrimSliderBounds();
    this.renderWaveform();
    this.renderCuratedTracksList();
    this.renderTrendingTracksList();
    this.editor.showToast(`Selected curated audio: ${track.name}`);
  }

  private renderWaveform() {
    const canvas = this.container?.querySelector('#waveform-canvas') as HTMLCanvasElement;
    if (!canvas || !this.activeAudioBuffer) return;

    const ctx = canvas.getContext('2d')!;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const channelData = this.activeAudioBuffer.getChannelData(0);
    const step = Math.ceil(channelData.length / w);
    const amp = h / 2;

    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, '#6366f1');
    grad.addColorStop(0.5, '#4f46e5');
    grad.addColorStop(1, '#a855f7');
    ctx.fillStyle = grad;

    for (let i = 0; i < w; i++) {
      let min = 1.0;
      let max = -1.0;
      for (let j = 0; j < step; j++) {
        const datum = channelData[i * step + j];
        if (datum < min) min = datum;
        if (datum > max) max = datum;
      }
      const barH = Math.max(2, (max - min) * amp * 0.95);
      const y = (h - barH) / 2;
      ctx.fillRect(i, y, 1.8, barH);
    }

    this.updateWaveformSelectionBox();
  }

  private updateWaveformSelectionBox() {
    const overlay = this.container?.querySelector('#selection-overlay-7s') as HTMLElement;
    if (!overlay || !this.activeAudioBuffer) return;

    const duration = this.activeAudioBuffer.duration;
    const startRatio = this.audioStartTime / duration;
    const widthRatio = Math.min(1.0, this.clipDuration / duration);

    overlay.style.left = `${startRatio * 100}%`;
    overlay.style.width = `${widthRatio * 100}%`;

    const startVal = this.container?.querySelector('#time-start');
    const endVal = this.container?.querySelector('#time-end');
    if (startVal) startVal.textContent = this.formatSeconds(this.audioStartTime);
    if (endVal) endVal.textContent = this.formatSeconds(this.audioStartTime + this.clipDuration);
  }

  private formatSeconds(sec: number): string {
    const mins = Math.floor(sec / 60);
    const remainingSec = sec % 60;
    return `${mins.toString().padStart(2, '0')}:${remainingSec.toFixed(2).padStart(5, '0')}`;
  }

  private toggleAudioPlayback() {
    if (this.isAudioPlaying) {
      this.stopAudio();
    } else {
      this.playAudioLoop();
    }
  }

  private playAudioLoop() {
    if (!this.activeAudioBuffer) return;

    const ctx = AudioHelper.getAudioContext();
    this.stopAudio();

    this.slicedAudioBuffer = AudioHelper.sliceAudio(
      this.activeAudioBuffer,
      this.audioStartTime,
      this.clipDuration
    );

    this.audioSourceNode = ctx.createBufferSource();
    this.audioSourceNode.buffer = this.slicedAudioBuffer;
    this.audioSourceNode.loop = true;
    this.audioSourceNode.connect(ctx.destination);
    this.audioSourceNode.start();

    this.isAudioPlaying = true;
    const playBtn = this.container?.querySelector('#btn-play-audio');
    const playIcon = this.container?.querySelector('#play-icon');
    const playText = this.container?.querySelector('#play-text');
    if (playIcon) playIcon.textContent = '⏸';
    if (playText) playText.textContent = 'Pause Loop';
    playBtn?.classList.add('playing');
  }

  private stopAudio() {
    if (this.audioSourceNode) {
      try {
        this.audioSourceNode.stop();
        this.audioSourceNode.disconnect();
      } catch (_) {}
      this.audioSourceNode = null;
    }
    this.isAudioPlaying = false;
    const playBtn = this.container?.querySelector('#btn-play-audio');
    const playIcon = this.container?.querySelector('#play-icon');
    const playText = this.container?.querySelector('#play-text');
    if (playIcon) playIcon.textContent = '▶';
    if (playText) playText.textContent = 'Play 7s Loop';
    playBtn?.classList.remove('playing');
  }

  private syncLogoSliders() {
    if (!this.container) return;
    const isPortrait = this.orientation === 'portrait';
    if (isPortrait) {
      const sY = this.container.querySelector('#slider-port-logo-y') as HTMLInputElement;
      const vY = this.container.querySelector('#val-port-logo-y');
      if (sY) sY.value = this.portraitLogoY.toString();
      if (vY) vY.textContent = `${this.portraitLogoY}px`;
    } else {
      const sX = this.container.querySelector('#slider-land-logo-x') as HTMLInputElement;
      const vX = this.container.querySelector('#val-land-logo-x');
      const sY = this.container.querySelector('#slider-land-logo-y') as HTMLInputElement;
      const vY = this.container.querySelector('#val-land-logo-y');
      if (sX) sX.value = this.landscapeLogoX.toString();
      if (vX) vX.textContent = `${this.landscapeLogoX}px`;
      if (sY) sY.value = this.landscapeLogoY.toString();
      if (vY) vY.textContent = `${this.landscapeLogoY}px`;
    }
  }

  private attachEvents(overlay: HTMLElement) {
    overlay.querySelector('#close-overlay-studio')?.addEventListener('click', () => this.close());

    // 4-Step Tab buttons
    overlay.querySelectorAll('.studio-tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tab = (e.currentTarget as HTMLElement).dataset.tab as any;
        this.switchTab(tab);
      });
    });

    // Step Flow Buttons
    overlay.querySelector('#btn-goto-step2')?.addEventListener('click', () => {
      this.switchTab('landscape');
      this.copyFromPortrait();
    });
    overlay.querySelector('#btn-goto-step3')?.addEventListener('click', () => this.switchTab('audio'));
    overlay.querySelector('#btn-goto-step4')?.addEventListener('click', () => this.switchTab('export'));
    overlay.querySelector('#btn-backto-step1')?.addEventListener('click', () => this.switchTab('portrait'));
    overlay.querySelector('#btn-backto-step2')?.addEventListener('click', () => this.switchTab('landscape'));

    // Copy from portrait button
    overlay.querySelector('#btn-copy-from-portrait')?.addEventListener('click', () => {
      this.copyFromPortrait();
    });

    // Toggle sample guest photo
    overlay.querySelector('#chk-preview-guest')?.addEventListener('change', (e: any) => {
      this.previewWithSampleGuest = e.target.checked;
      this.updatePreviewCanvas();
    });

    // Toggle API key visibility
    overlay.querySelector('#btn-toggle-key-vis')?.addEventListener('click', () => {
      const input = overlay.querySelector('#grok-api-key') as HTMLInputElement;
      if (input) input.type = input.type === 'password' ? 'text' : 'password';
    });

    // Portrait window margin sliders
    overlay.querySelector('#slider-top-inset')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.windowConfig.topInset = val;
      const label = overlay.querySelector('#val-top-inset');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    overlay.querySelector('#slider-bottom-inset')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.windowConfig.bottomInset = val;
      const label = overlay.querySelector('#val-bottom-inset');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    overlay.querySelector('#slider-sides-inset')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.windowConfig.leftInset = val;
      this.windowConfig.rightInset = val;
      const label = overlay.querySelector('#val-sides-inset');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    overlay.querySelector('#slider-corner-radius')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.windowConfig.cornerRadius = val;
      const label = overlay.querySelector('#val-corner-radius');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    // Landscape window cutout sliders
    overlay.querySelector('#slider-land-tb-inset')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.landscapeWindowConfig.topInset = val;
      this.landscapeWindowConfig.bottomInset = val;
      const label = overlay.querySelector('#val-land-tb-inset');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    overlay.querySelector('#slider-land-lr-inset')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.landscapeWindowConfig.leftInset = val;
      this.landscapeWindowConfig.rightInset = val;
      const label = overlay.querySelector('#val-land-lr-inset');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    overlay.querySelector('#slider-land-corner')?.addEventListener('input', (e: any) => {
      const val = parseInt(e.target.value);
      this.landscapeWindowConfig.cornerRadius = val;
      const label = overlay.querySelector('#val-land-corner');
      if (label) label.textContent = `${val}px`;
      this.updateProcessedCanvases();
    });

    // Logo sample presets
    overlay.querySelector('#btn-logo-preset-trunk')?.addEventListener('click', () => {
      this.createDefaultLogo('trunk_treat');
      const label = overlay.querySelector('#label-logo-name');
      if (label) label.textContent = this.logoName;
    });
    overlay.querySelector('#btn-logo-preset-spider')?.addEventListener('click', () => {
      this.createDefaultLogo('spider_vip');
      const label = overlay.querySelector('#label-logo-name');
      if (label) label.textContent = this.logoName;
    });
    overlay.querySelector('#btn-logo-preset-disco')?.addEventListener('click', () => {
      this.createDefaultLogo('disco_360');
      const label = overlay.querySelector('#label-logo-name');
      if (label) label.textContent = this.logoName;
    });

    // Custom Logo File Upload
    const logoFileInput = overlay.querySelector('#logo-file-input') as HTMLInputElement;
    overlay.querySelector('#btn-upload-logo')?.addEventListener('click', () => logoFileInput?.click());
    logoFileInput?.addEventListener('change', () => {
      const file = logoFileInput.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            this.logoImg = img;
            this.logoName = file.name;
            const label = overlay.querySelector('#label-logo-name');
            if (label) label.textContent = file.name;
            this.updatePreviewCanvas();
            this.editor.showToast(`Uploaded logo: ${file.name}`);
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    });

    // Portrait Logo Position Sliders
    overlay.querySelector('#slider-port-logo-y')?.addEventListener('input', (e: any) => {
      this.portraitLogoY = parseInt(e.target.value);
      const l = overlay.querySelector('#val-port-logo-y');
      if (l) l.textContent = `${this.portraitLogoY}px`;
      this.updatePreviewCanvas();
    });

    overlay.querySelector('#slider-port-logo-scale')?.addEventListener('input', (e: any) => {
      this.portraitLogoScale = parseInt(e.target.value) / 100;
      const l = overlay.querySelector('#val-port-logo-scale');
      if (l) l.textContent = `${e.target.value}%`;
      this.updatePreviewCanvas();
    });

    // Portrait Logo Quick Chips
    overlay.querySelector('#pos-port-bottom')?.addEventListener('click', () => {
      this.portraitLogoX = 540;
      this.portraitLogoY = 1720;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-port-top')?.addEventListener('click', () => {
      this.portraitLogoX = 540;
      this.portraitLogoY = 180;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-port-top-left')?.addEventListener('click', () => {
      this.portraitLogoX = 220;
      this.portraitLogoY = 180;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-port-top-right')?.addEventListener('click', () => {
      this.portraitLogoX = 860;
      this.portraitLogoY = 180;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });

    // Landscape Logo Position Sliders
    overlay.querySelector('#slider-land-logo-x')?.addEventListener('input', (e: any) => {
      this.landscapeLogoX = parseInt(e.target.value);
      const l = overlay.querySelector('#val-land-logo-x');
      if (l) l.textContent = `${this.landscapeLogoX}px`;
      this.updatePreviewCanvas();
    });

    overlay.querySelector('#slider-land-logo-y')?.addEventListener('input', (e: any) => {
      this.landscapeLogoY = parseInt(e.target.value);
      const l = overlay.querySelector('#val-land-logo-y');
      if (l) l.textContent = `${this.landscapeLogoY}px`;
      this.updatePreviewCanvas();
    });

    overlay.querySelector('#slider-land-logo-scale')?.addEventListener('input', (e: any) => {
      this.landscapeLogoScale = parseInt(e.target.value) / 100;
      const l = overlay.querySelector('#val-land-logo-scale');
      if (l) l.textContent = `${e.target.value}%`;
      this.updatePreviewCanvas();
    });

    // Landscape Logo Quick Position Chips ("kinda move it around so they can take a shot at the landscape 1")
    overlay.querySelector('#pos-land-top-center')?.addEventListener('click', () => {
      this.landscapeLogoX = 960;
      this.landscapeLogoY = 120;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-land-top-left')?.addEventListener('click', () => {
      this.landscapeLogoX = 240;
      this.landscapeLogoY = 120;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-land-top-right')?.addEventListener('click', () => {
      this.landscapeLogoX = 1680;
      this.landscapeLogoY = 120;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-land-bottom-center')?.addEventListener('click', () => {
      this.landscapeLogoX = 960;
      this.landscapeLogoY = 960;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-land-bottom-left')?.addEventListener('click', () => {
      this.landscapeLogoX = 240;
      this.landscapeLogoY = 960;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });
    overlay.querySelector('#pos-land-bottom-right')?.addEventListener('click', () => {
      this.landscapeLogoX = 1680;
      this.landscapeLogoY = 960;
      this.syncLogoSliders();
      this.updatePreviewCanvas();
    });

    // Direct Interactive Logo Dragging on the Preview Canvas
    const previewCanvas = overlay.querySelector('#studio-preview-canvas') as HTMLCanvasElement;
    if (previewCanvas) {
      previewCanvas.addEventListener('mousedown', (e) => {
        const rect = previewCanvas.getBoundingClientRect();
        const scaleX = previewCanvas.width / rect.width;
        const scaleY = previewCanvas.height / rect.height;
        const mouseX = (e.clientX - rect.left) * scaleX;
        const mouseY = (e.clientY - rect.top) * scaleY;

        const isPortrait = this.orientation === 'portrait';
        const curX = isPortrait ? this.portraitLogoX : this.landscapeLogoX;
        const curY = isPortrait ? this.portraitLogoY : this.landscapeLogoY;
        const curScale = isPortrait ? this.portraitLogoScale : this.landscapeLogoScale;
        const baseW = (isPortrait ? 400 : 380) * curScale;
        const baseH = baseW * (this.logoImg ? this.logoImg.naturalHeight / this.logoImg.naturalWidth : 0.6);

        if (Math.abs(mouseX - curX) <= baseW / 2 + 40 && Math.abs(mouseY - curY) <= baseH / 2 + 40) {
          this.isDraggingLogo = true;
          this.dragStartX = mouseX;
          this.dragStartY = mouseY;
          this.logoStartPosX = curX;
          this.logoStartPosY = curY;
          previewCanvas.style.cursor = 'grabbing';
        }
      });

      window.addEventListener('mousemove', (e) => {
        if (!this.isDraggingLogo) return;
        const rect = previewCanvas.getBoundingClientRect();
        const scaleX = previewCanvas.width / rect.width;
        const scaleY = previewCanvas.height / rect.height;
        const mouseX = (e.clientX - rect.left) * scaleX;
        const mouseY = (e.clientY - rect.top) * scaleY;

        const dx = mouseX - this.dragStartX;
        const dy = mouseY - this.dragStartY;

        if (this.orientation === 'portrait') {
          this.portraitLogoX = Math.round(Math.max(50, Math.min(1030, this.logoStartPosX + dx)));
          this.portraitLogoY = Math.round(Math.max(50, Math.min(1870, this.logoStartPosY + dy)));
        } else {
          this.landscapeLogoX = Math.round(Math.max(50, Math.min(1870, this.logoStartPosX + dx)));
          this.landscapeLogoY = Math.round(Math.max(50, Math.min(1030, this.logoStartPosY + dy)));
        }
        this.syncLogoSliders();
        this.updatePreviewCanvas();
      });

      window.addEventListener('mouseup', () => {
        if (this.isDraggingLogo) {
          this.isDraggingLogo = false;
          previewCanvas.style.cursor = 'default';
        }
      });
    }

    // Generate AI Border with Grok
    overlay.querySelector('#btn-generate-border')?.addEventListener('click', () => {
      this.generateGrokBorder();
    });

    // Upload custom border image
    const borderFileInput = overlay.querySelector('#border-file-input') as HTMLInputElement;
    overlay.querySelector('#btn-upload-border')?.addEventListener('click', () => borderFileInput?.click());
    borderFileInput?.addEventListener('change', () => {
      const file = borderFileInput.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            this.rawBorderImg = img;
            this.updateProcessedCanvases();
            this.editor.showToast(`Loaded border image: ${file.name}`);
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }
    });

    // Audio Playback
    overlay.querySelector('#btn-play-audio')?.addEventListener('click', () => this.toggleAudioPlayback());

    // Audio file drop / upload
    const audioInput = overlay.querySelector('#audio-file-input') as HTMLInputElement;
    const audioDropZone = overlay.querySelector('#upload-audio-zone');
    audioDropZone?.addEventListener('click', () => audioInput?.click());
    audioInput?.addEventListener('change', () => {
      const file = audioInput.files?.[0];
      if (file) this.loadCustomAudioFile(file);
    });

    // Download Actions
    overlay.querySelector('#btn-download-zip')?.addEventListener('click', () => this.generateZip());
    overlay.querySelector('#btn-download-portrait')?.addEventListener('click', () => this.downloadPortrait());
    overlay.querySelector('#btn-download-landscape')?.addEventListener('click', () => this.downloadLandscape());
    overlay.querySelector('#btn-export-audio-only')?.addEventListener('click', () => this.downloadAudioClip());
    overlay.querySelector('#btn-download-audio-tab4')?.addEventListener('click', () => this.downloadAudioClip());
    overlay.querySelector('#btn-open-in-ironcrop')?.addEventListener('click', () => this.openInIronCropCanvas());
  }

  private switchTab(tab: 'portrait' | 'landscape' | 'audio' | 'export') {
    this.activeTab = tab;
    this.orientation = tab === 'landscape' ? 'landscape' : 'portrait';

    const overlay = this.container;
    if (!overlay) return;

    overlay.querySelectorAll('.studio-tab-btn').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.tab === tab);
    });

    overlay.querySelectorAll('.tab-page').forEach(page => {
      (page as HTMLElement).style.display = 'none';
      page.classList.remove('active');
    });

    const activePage = overlay.querySelector(`#page-${tab}`) as HTMLElement;
    if (activePage) {
      activePage.style.display = 'block';
      activePage.classList.add('active');
    }

    const title = overlay.querySelector('#preview-mode-title');
    const res = overlay.querySelector('#preview-res-indicator');
    if (title && res) {
      if (this.orientation === 'portrait') {
        title.textContent = 'Live Preview (Portrait 1080 × 1920)';
        res.textContent = '1080 × 1920 px • Transparent Window • Moveable Logo';
      } else {
        title.textContent = 'Live Preview (Landscape 1920 × 1080)';
        res.textContent = '1920 × 1080 px • 16:9 Cutout • Drag Logo to Position';
      }
    }

    this.updatePreviewCanvas();
  }

  private copyFromPortrait() {
    if (!this.portraitCanvas) return;
    this.landscapeCanvas = BorderGenerator.adaptToLandscape(this.portraitCanvas, this.landscapeWindowConfig);
    // Initial position for landscape: top center
    this.landscapeLogoX = 960;
    this.landscapeLogoY = 120;
    this.syncLogoSliders();
    this.updatePreviewCanvas();
    this.editor.showToast('📋 Adapted frame & copied logo to Landscape! Drag logo to position.');
  }

  private async generateGrokBorder() {
    const keyInput = this.container?.querySelector('#grok-api-key') as HTMLInputElement;
    const apiKey = keyInput?.value?.trim() || BorderGenerator.getStoredApiKey();

    if (!apiKey) {
      this.editor.showToast('Please enter your xAI Grok API key');
      return;
    }

    const theme = (this.container?.querySelector('#border-theme') as HTMLInputElement)?.value || '';
    const top = (this.container?.querySelector('#border-top') as HTMLInputElement)?.value || '';
    const sides = (this.container?.querySelector('#border-sides') as HTMLInputElement)?.value || '';
    const bottom = (this.container?.querySelector('#border-bottom') as HTMLInputElement)?.value || '';

    const config: BorderPromptConfig = {
      theme,
      topElements: top,
      sideElements: sides,
      bottomElements: bottom,
      aspectRatio: this.orientation === 'portrait' ? '9:16' : '16:9'
    };

    const genBtn = this.container?.querySelector('#btn-generate-border') as HTMLButtonElement;
    const icon = this.container?.querySelector('#gen-icon');
    const text = this.container?.querySelector('#gen-text');

    if (genBtn) genBtn.disabled = true;
    if (icon) icon.textContent = '⏳';
    if (text) text.textContent = 'Grok is generating AI border...';

    this.editor.showToast('Generating high-res photobooth border with Grok AI...');

    try {
      const img = await BorderGenerator.generateBorder(apiKey, config);
      this.rawBorderImg = img;
      this.updateProcessedCanvases();
      this.editor.showToast('✨ Grok AI Border generated successfully!');
    } catch (err: any) {
      console.error('Grok generation failed:', err);
      this.editor.showToast(`Generation error: ${err.message || 'Check API key or network'}`);
    } finally {
      if (genBtn) genBtn.disabled = false;
      if (icon) icon.textContent = '⚡';
      if (text) text.textContent = 'Generate Portrait Border with Grok';
    }
  }

  private async loadCustomAudioFile(file: File) {
    this.stopTrackPreview();
    this.stopAudio();
    this.editor.showToast(`Decoding ${file.name}...`);
    try {
      const buffer = await AudioHelper.decodeAudioFile(file);
      this.activeAudioBuffer = buffer;
      this.slicedAudioBuffer = AudioHelper.sliceAudio(buffer, 0, this.clipDuration);
      this.audioStartTime = 0;
      this.selectedTrackName = file.name;
      this.selectedTrackId = 'custom_upload';
      this.selectedTrackArtwork = '';

      this.updateActiveTrackBanner();
      this.updateTrimSliderBounds();
      this.renderWaveform();
      this.renderCuratedTracksList();
      this.renderTrendingTracksList();
      this.editor.showToast(`Audio loaded! Exact 7.0-second slice ready.`);
    } catch (err) {
      console.error(err);
      this.editor.showToast('Could not decode audio file');
    }
  }

  /**
   * Produce the final composite canvas with border cutout AND positioned logo
   */
  private getFinalCompositeCanvas(type: 'portrait' | 'landscape'): HTMLCanvasElement {
    const isPortrait = type === 'portrait';
    const targetW = isPortrait ? 1080 : 1920;
    const targetH = isPortrait ? 1920 : 1080;
    const baseCanvas = isPortrait ? this.portraitCanvas : this.landscapeCanvas;

    const out = document.createElement('canvas');
    out.width = targetW;
    out.height = targetH;
    const ctx = out.getContext('2d')!;

    // 1. Draw base transparent border
    if (baseCanvas) {
      ctx.drawImage(baseCanvas, 0, 0, targetW, targetH);
    }

    // 2. Composite the logo at exact coordinates
    if (this.logoImg && this.logoImg.complete) {
      const curX = isPortrait ? this.portraitLogoX : this.landscapeLogoX;
      const curY = isPortrait ? this.portraitLogoY : this.landscapeLogoY;
      const curScale = isPortrait ? this.portraitLogoScale : this.landscapeLogoScale;

      const baseW = (isPortrait ? 400 : 380) * curScale;
      const ratio = this.logoImg.naturalHeight / this.logoImg.naturalWidth;
      const baseH = baseW * ratio;
      const x = curX - baseW / 2;
      const y = curY - baseH / 2;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 12;
      ctx.drawImage(this.logoImg, x, y, baseW, baseH);
      ctx.restore();
    }

    return out;
  }

  private downloadPortrait() {
    const canvas = this.getFinalCompositeCanvas('portrait');
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'overlay_portrait_1080x1920.png';
        a.click();
        URL.revokeObjectURL(url);
        this.editor.showToast('Downloaded overlay_portrait_1080x1920.png');
      }
    }, 'image/png');
  }

  private downloadLandscape() {
    const canvas = this.getFinalCompositeCanvas('landscape');
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'overlay_landscape_1920x1080.png';
        a.click();
        URL.revokeObjectURL(url);
        this.editor.showToast('Downloaded overlay_landscape_1920x1080.png');
      }
    }, 'image/png');
  }

  private downloadAudioClip() {
    if (!this.activeAudioBuffer) {
      this.editor.showToast('No audio loaded to download');
      return;
    }
    const sliced = AudioHelper.sliceAudio(this.activeAudioBuffer, this.audioStartTime, this.clipDuration);
    const wavBlob = AudioHelper.audioBufferToWavBlob(sliced);
    const url = URL.createObjectURL(wavBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audio_clip_7s.wav';
    a.click();
    URL.revokeObjectURL(url);
    this.editor.showToast('Downloaded 7-second audio_clip_7s.wav');
  }

  private async generateZip() {
    this.editor.showToast('Building photobooth package (.ZIP)...');

    const zip = new JSZip();

    // 1. Portrait PNG
    const pCanvas = this.getFinalCompositeCanvas('portrait');
    const pBlob = await new Promise<Blob | null>(res => pCanvas.toBlob(res, 'image/png'));
    if (pBlob) {
      zip.file('overlay_portrait_1080x1920.png', pBlob);
    }

    // 2. Landscape PNG
    const lCanvas = this.getFinalCompositeCanvas('landscape');
    const lBlob = await new Promise<Blob | null>(res => lCanvas.toBlob(res, 'image/png'));
    if (lBlob) {
      zip.file('overlay_landscape_1920x1080.png', lBlob);
    }

    // 3. Audio 7s WAV
    if (this.activeAudioBuffer) {
      const sliced = AudioHelper.sliceAudio(this.activeAudioBuffer, this.audioStartTime, this.clipDuration);
      const wavBlob = AudioHelper.audioBufferToWavBlob(sliced);
      zip.file('audio_clip_7s.wav', wavBlob);
    }

    // 4. Package Info JSON
    const metadata = {
      package: 'Iron Crop Photobooth & Reel Package',
      createdAt: new Date().toISOString(),
      audioTrack: this.selectedTrackName,
      audioDurationSec: 7.0,
      audioStartTimeSec: this.audioStartTime,
      portraitDimensions: '1080x1920 (9:16)',
      landscapeDimensions: '1920x1080 (16:9)',
      logoName: this.logoName,
      portraitLogoPos: { x: this.portraitLogoX, y: this.portraitLogoY, scale: this.portraitLogoScale },
      landscapeLogoPos: { x: this.landscapeLogoX, y: this.landscapeLogoY, scale: this.landscapeLogoScale }
    };
    zip.file('package_info.json', JSON.stringify(metadata, null, 2));

    const zipContent = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipContent);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reel_photobooth_package.zip';
    a.click();
    URL.revokeObjectURL(url);
    this.editor.showToast('✨ Package downloaded: reel_photobooth_package.zip');
  }

  private openInIronCropCanvas() {
    const canvas = this.getFinalCompositeCanvas('portrait');
    const dataUrl = canvas.toDataURL('image/png');
    this.editor.openImageFromDataUrl(dataUrl, 'Photobooth Frame 1080×1920');
    this.close();
    this.editor.showToast('Opened photobooth frame in main editor!');
  }

  public close() {
    this.stopAudio();
    this.stopTrackPreview();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }
}
